import { useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileText, Upload, Download, Trash2, Loader2, Sparkles } from "lucide-react";

const itDate = (s) => {
  if (!s) return "—";
  const p = String(s).slice(0, 10).split("-");
  return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : s;
};

const Field = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-sm font-medium">{value || "—"}</div>
  </div>
);

export default function AgencyContratto({ agencyId, agency, canEdit, onExtracted, onChanged }) {
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const current = agency.agency_contract;
  const history = agency.agency_contracts || [];

  const upload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) { toast.error("Carica un file PDF"); return; }
    setBusy(true);
    const t = toast.loading("Caricamento e analisi del contratto in corso…");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("estrai", "1");
      const r = await api.post(`/agencies/${agencyId}/contratto`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Contratto caricato", { id: t });
      onChanged?.();
      if (r.data?.estratto && Object.keys(r.data.estratto).length) {
        toast.info("Condizioni estratte dal contratto: verifica e conferma.");
        onExtracted?.(r.data.estratto);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel caricamento", { id: t });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const download = async (fid, name) => {
    try {
      const r = await api.get(`/agencies/${agencyId}/contratto/${fid}`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = name || "contratto.pdf"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Errore nel download"); }
  };

  const del = async (fid) => {
    if (!window.confirm("Eliminare questa versione del contratto?")) return;
    try { await api.delete(`/agencies/${agencyId}/contratto/${fid}`); toast.success("Versione eliminata"); onChanged?.(); }
    catch { toast.error("Errore nell'eliminazione"); }
  };

  return (
    <Card data-testid="agency-contract-section" className="p-5">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-base font-bold tracking-tight sm:text-lg"><FileText className="h-5 w-5 text-primary" />Contratto di agenzia</h2>
        {canEdit && (
          <>
            <input ref={fileRef} type="file" accept="application/pdf" hidden onChange={(e) => upload(e.target.files?.[0])} data-testid="contract-file-input" />
            <Button size="sm" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="upload-contract-button">
              {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Upload className="mr-1.5 h-4 w-4" />}Carica contratto (PDF)
            </Button>
          </>
        )}
      </div>
      {canEdit && (
        <p className="mb-3 inline-flex items-center gap-1 text-[11px] text-muted-foreground">
          <Sparkles className="h-3.5 w-3.5 text-primary" />Al caricamento estraiamo automaticamente le condizioni economiche da rivedere e confermare.
        </p>
      )}
      {current ? (
        <div className="grid gap-x-6 gap-y-3 sm:grid-cols-4">
          <Field label="Data firma" value={itDate(current.data_firma)} />
          <Field label="Decorrenza" value={itDate(current.data_decorrenza)} />
          <Field label="Scadenza" value={current.data_scadenza ? itDate(current.data_scadenza) : "Indeterminato"} />
          <Field label="Durata" value={current.durata} />
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Nessun contratto caricato.</p>
      )}
      {history.length > 0 && (
        <div className="mt-5">
          <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Storico versioni</div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead><TableHead>Firma</TableHead><TableHead>Scadenza</TableHead>
                <TableHead>Caricato</TableHead><TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {history.map((c) => (
                <TableRow key={c.id} data-testid="contract-version-row">
                  <TableCell className="max-w-[220px] truncate font-medium">{c.filename}</TableCell>
                  <TableCell className="text-sm">{itDate(c.data_firma)}</TableCell>
                  <TableCell className="text-sm">{c.data_scadenza ? itDate(c.data_scadenza) : "—"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{itDate(c.uploaded_at)}</TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" onClick={() => download(c.id, c.filename)} data-testid={`download-contract-${c.id}`}><Download className="h-4 w-4" /></Button>
                    {canEdit && <Button size="icon" variant="ghost" onClick={() => del(c.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </Card>
  );
}
